import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoadingButton from "@mui/lab/LoadingButton";
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Paper from '../../components/Paper/CustomPaper';
import CustomTextField from '../../components/FormFields/CustomTextField';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearState } from '../../store/passwordSlice';
import { logout } from '../../store/authSlice';
import { resetPassword } from '../../store/passwordThunk';
import { ApiStatus } from '../../utils/api.types';
import { IResetPasswordFormInputs } from '../../utils/password.types';

// Schema to validate reset password form.
const schema = yup.object().shape({
    email: yup.string().email(),
    oldPassword: yup.string().required('Current password is required.'),
    newPassword: yup.string().required('New password is required.'),
    confirmPassword: yup.string().required('Confirm Password is required')
        .oneOf([yup.ref('newPassword')], 'Passwords must match')
  });

/**
 * Component for Reset Password.
 */
const ResetPassword = () => {
    const methods = useForm<IResetPasswordFormInputs>({
        resolver: yupResolver(schema),
    });
    
    const [searchParams, setSearchParams] = useSearchParams();
    const { status } = useAppSelector((state) => state.password);
    const { isLoggedIn, user } = useAppSelector((state) => state.auth);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    /**
     * Method to handle reset password form submit.
     */
    const onSubmit = (data: IResetPasswordFormInputs) => {
        // Dispatch reset password action with reset password form data.
        if (!data.email) {
            data.email = user.email ?? '';
        }
        dispatch(resetPassword(data));
    };

    /**
     * Clear state on reset password page load.
     */
    useEffect(() => {
        return () => {
            dispatch(clearState());
        };
    }, [dispatch]);

    /**
     * Update UI based on the state(Success or Error).
     */ 
    useEffect(() => {
        if (status === ApiStatus.FAILED) {
            dispatch(clearState());
        }
        let token: any = searchParams.get('inviteToken');
        let qp = '';
        if (token) {
            qp = '?inviteToken='+token;
        }
        // If reset password is successful then logout the user and then 
        // redirect the user to login page.
        if (status === ApiStatus.SUCCESS) {
            dispatch(logout());
            dispatch(clearState());
            navigate('/signin'+qp, { replace: true });
        }
    }, [status, dispatch, navigate ]);

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Paper height={450}> 
                <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                    <LockOutlinedIcon />
                </Avatar>
                <Typography component="h1" variant="h5">
                    Reset Password
                </Typography>
                <FormProvider {...methods}>
                    <Box
                        component="form"
                        onSubmit={methods.handleSubmit(onSubmit)} 
                        noValidate
                        sx={{ mt: 1 }}
                    >
                        {!isLoggedIn && <CustomTextField
                            label='Email'
                            name='email'
                            required
                        />}
                        <CustomTextField
                            label='Current Password'
                            name='oldPassword'
                            type={showOldPassword ? 'text' : 'password'}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                                            {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <CustomTextField
                            label='New Password'
                            name='newPassword'
                            type={showNewPassword ? 'text' : 'password'}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <CustomTextField
                            label='Confirm Password'
                            name='confirmPassword'
                            type={showConfirmPassword ? 'text' : 'password'}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <LoadingButton
                            type="submit"
                            fullWidth
                            loading={status === ApiStatus.LOADING}
                            variant="contained"
                            color='secondary'
                            sx={{ mt: 3, mb: 2 }}
                        >
                            Submit
                        </LoadingButton>
                    </Box>
                </FormProvider>
            </Paper>
        </Container>
    )
}

export default ResetPassword;